import React, { useEffect, useState } from "react";
import { Alert, Button, Drawer, Empty, Input, Space, Tag, Typography } from "antd";
import { AMOUNT_DELTA_TYPE } from "../../utils/reviewPayloadSemantics";

const { Text } = Typography;
const { TextArea } = Input;

const formatAmount = (value, precision = 6) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value ?? "-");
    return numericValue.toFixed(precision);
};

const formatSignedAmount = (value, precision = 6) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    const sign = numericValue > 0 ? "+" : "";
    return `${sign}${numericValue.toFixed(precision)}`;
};

const issueKey = (issue) => `${issue.fieldPath}:${issue.deltaType}`;

const initialResolution = (issue) => ({
    formulaDescription: issue.formulaDescription || "",
    pricingBasis: issue.pricingBasis || "",
    reason: issue.reason || "",
});

const isResolutionComplete = (issue, resolution = {}) => {
    if (issue.deltaType === AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING) return true;
    if (issue.deltaType === AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING) {
        return Boolean(
            String(resolution.formulaDescription || "").trim()
            && String(resolution.pricingBasis || "").trim()
            && String(resolution.reason || "").trim(),
        );
    }
    if (issue.deltaType === AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED) {
        return Boolean(String(resolution.reason || "").trim());
    }
    return false;
};

const TAG_CONFIG = {
    [AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING]: { color: "blue", label: "尾差" },
    [AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING]: { color: "gold", label: "复杂计价" },
    [AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED]: { color: "orange", label: "人工审核" },
};

const lineLabel = (issue) => issue.description || issue.productCode || `第 ${(issue.itemIndex ?? 0) + 1} 行`;

const AmountIssuePanel = ({ open, issues = [], readOnly = false, onClose, onApply }) => {
    const [resolutions, setResolutions] = useState({});

    useEffect(() => {
        if (!open) {
            setResolutions({});
            return;
        }
        setResolutions((current) => Object.fromEntries(
            issues.map((issue) => {
                const key = issueKey(issue);
                return [key, current[key] || initialResolution(issue)];
            }),
        ));
    }, [issues, open]);

    const updateResolution = (issue, field, value) => {
        const key = issueKey(issue);
        setResolutions((current) => ({
            ...current,
            [key]: {
                ...(current[key] || initialResolution(issue)),
                [field]: value,
            },
        }));
    };

    const allReady = issues.every((issue) => isResolutionComplete(issue, resolutions[issueKey(issue)]));

    const applyIssues = (selectedIssues) => {
        onApply?.(selectedIssues.map((issue) => ({
            issue,
            resolution: resolutions[issueKey(issue)] || initialResolution(issue),
        })));
    };

    return (
        <Drawer
            title={`审核前待处理项（${issues.length}）`}
            open={open}
            onClose={onClose}
            width="min(820px, 100vw)"
            extra={(
                <Button
                    type="primary"
                    disabled={readOnly || issues.length === 0 || !allReady}
                    onClick={() => applyIssues(issues)}
                >
                    全部应用
                </Button>
            )}
        >
            <Alert
                type="info"
                showIcon
                message="处理决定只覆盖当前行的金额乘法差异"
                description="尾差自动调整审核单价；复杂计价和人工审核按票面金额入账，需填写说明。"
                style={{ marginBottom: 12 }}
            />

            {issues.length === 0 ? (
                <Empty description="当前没有待处理的金额问题" />
            ) : (
                <div>
                    {issues.map((issue) => {
                        const key = issueKey(issue);
                        const resolution = resolutions[key] || initialResolution(issue);
                        const tagConfig = TAG_CONFIG[issue.deltaType] || { color: "default", label: "未知" };
                        const ready = isResolutionComplete(issue, resolution);
                        const label = lineLabel(issue);
                        const isFormulaIssue = issue.deltaType === AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING;
                        const isManualReviewIssue = issue.deltaType === AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED;
                        return (
                            <div
                                key={key}
                                style={{ borderBottom: "1px solid #f0f0f0", padding: "12px 0" }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                    <Space size={8}>
                                        <Text strong style={{ maxWidth: 320 }} ellipsis={{ tooltip: label }}>{label}</Text>
                                        {issue.staleDecision && (
                                            <Tag color="warning" style={{ margin: 0 }}>待重新确认</Tag>
                                        )}
                                        <Tag color={tagConfig.color} style={{ margin: 0 }}>{tagConfig.label}</Tag>
                                    </Space>
                                    <Text type="danger" strong style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                        差额 {formatSignedAmount(issue.amountDelta)}
                                    </Text>
                                </div>

                                <div style={{ marginBottom: 8, fontSize: 13, color: "#8c8c8c" }}>
                                    {formatAmount(issue.quantity)} × {formatAmount(issue.unitPrice ?? issue.displayedUnitPrice, 12)} = {formatAmount(issue.calculatedAmount)}
                                    <span style={{ margin: "0 6px" }}>|</span>
                                    票面 {formatAmount(issue.declaredAmount)}
                                    {issue.deltaType === AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING && (
                                        <>
                                            <span style={{ margin: "0 6px" }}>|</span>
                                            建议单价 <Text code style={{ fontSize: 12 }}>{formatAmount(issue.impliedUnitPrice, 12)}</Text>
                                        </>
                                    )}
                                </div>

                                {isFormulaIssue ? (
                                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                        <Input
                                            aria-label={`${label}计价公式或授权例外`}
                                            value={resolution.formulaDescription}
                                            onChange={(event) => updateResolution(issue, "formulaDescription", event.target.value)}
                                            placeholder="计价公式或授权例外说明，例如：MTOW 分档最低收费"
                                            disabled={readOnly}
                                        />
                                        <Input
                                            aria-label={`${label}计价依据`}
                                            value={resolution.pricingBasis}
                                            onChange={(event) => updateResolution(issue, "pricingBasis", event.target.value)}
                                            placeholder="计价依据，例如：合同第 4.2 条、MTOW 186 吨"
                                            disabled={readOnly}
                                        />
                                        <TextArea
                                            aria-label={`${label}审核说明`}
                                            rows={2}
                                            value={resolution.reason}
                                            onChange={(event) => updateResolution(issue, "reason", event.target.value)}
                                            placeholder="说明为何接受票面金额"
                                            disabled={readOnly}
                                        />
                                        <Button
                                            type="primary"
                                            ghost
                                            disabled={readOnly || !ready}
                                            onClick={() => applyIssues([issue])}
                                        >
                                            接受票面金额
                                        </Button>
                                    </Space>
                                ) : isManualReviewIssue ? (
                                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                        {issue.authorityReason && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>{issue.authorityReason}</Text>
                                        )}
                                        <TextArea
                                            aria-label={`${label}人工审核说明`}
                                            rows={2}
                                            value={resolution.reason}
                                            onChange={(event) => updateResolution(issue, "reason", event.target.value)}
                                            placeholder="请填写审核说明（必填），说明接受该差额的原因"
                                            disabled={readOnly}
                                        />
                                        <Button
                                            type="primary"
                                            ghost
                                            disabled={readOnly || !ready}
                                            onClick={() => applyIssues([issue])}
                                        >
                                            接受票面金额
                                        </Button>
                                    </Space>
                                ) : (
                                    <Button
                                        type="primary"
                                        ghost
                                        disabled={readOnly || !ready}
                                        onClick={() => applyIssues([issue])}
                                    >
                                        调整审核单价
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Drawer>
    );
};

export default AmountIssuePanel;
